
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * ExportedOrderState struct
 */
class ExportedOrderState {
  /**
   * Initialize struct
   * @param {!number=} orderId
   * @param {!number=} profit
   * @constructor
   */
  constructor (orderId = new UInt64(0, 0), profit = 0.0) {
    this.OrderId = orderId
    this.Profit = profit
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ExportedOrderState}
   * @param {!ExportedOrderState} other Other struct
   * @returns {!ExportedOrderState} This struct
   */
  copy (other) {
    if (other.OrderId != null) {
      this.OrderId = UInt64.fromNumber(other.OrderId)
    } else {
      this.OrderId = null
    }
    if (other.Profit != null) {
      this.Profit = other.Profit
    } else {
      this.Profit = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ExportedOrderState}
   * @returns {!ExportedOrderState} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ExportedOrderStateModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ExportedOrderStateModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ExportedOrderState}
   * @param {!ExportedOrderState} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ExportedOrderState)) {
      throw new TypeError('Instance of ExportedOrderState is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ExportedOrderState}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      OrderId: ((this.OrderId != null) ? this.OrderId.toNumber() : null),
      Profit: ((this.Profit != null) ? this.Profit : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ExportedOrderState.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ExportedOrderState} other Object value
   * @returns {!ExportedOrderState} Created struct
   */
  static fromObject (other) {
    return new ExportedOrderState().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ExportedOrderState}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ExportedOrderState.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ExportedOrderState}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 12056
  }
}

export { ExportedOrderState };

/**
 * Fast Binary Encoding ExportedOrderState field model
 */
class FieldModelExportedOrderState extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.OrderId = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.Profit = new fbe.FieldModelDouble(buffer, this.OrderId.fbeOffset + this.OrderId.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelExportedOrderState}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelExportedOrderState}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.OrderId.fbeSize + this.Profit.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelExportedOrderState}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.OrderId.fbeExtra + this.Profit.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelExportedOrderState}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelExportedOrderState.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelExportedOrderState}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 12056
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelExportedOrderState}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelExportedOrderState}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.OrderId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.OrderId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrderId.fbeSize

    if ((fbeCurrentSize + this.Profit.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Profit.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Profit.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelExportedOrderState}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelExportedOrderState}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelExportedOrderState}
   * @param {!ExportedOrderState} fbeValue Default value, defaults is new ExportedOrderState()
   * @returns {!ExportedOrderState} ExportedOrderState value
   */
  get (fbeValue = new ExportedOrderState()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelExportedOrderState}
   * @param {!ExportedOrderState} fbeValue ExportedOrderState value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.OrderId.fbeSize) <= fbeStructSize) {
      fbeValue.OrderId = this.OrderId.get()
    } else {
      fbeValue.OrderId = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrderId.fbeSize

    if ((fbeCurrentSize + this.Profit.fbeSize) <= fbeStructSize) {
      fbeValue.Profit = this.Profit.get()
    } else {
      fbeValue.Profit = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Profit.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelExportedOrderState}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelExportedOrderState}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelExportedOrderState}
   * @param {!ExportedOrderState} fbeValue ExportedOrderState value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelExportedOrderState}
   * @param {ExportedOrderState} fbeValue ExportedOrderState value
   */
  setFields (fbeValue) {
    this.OrderId.set(fbeValue.OrderId)
    this.Profit.set(fbeValue.Profit)
  }
}

export { FieldModelExportedOrderState };

/**
 * Fast Binary Encoding ExportedOrderState model
 */
class ExportedOrderStateModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelExportedOrderState(this.buffer, 4)
  }

  /**
   * Get the ExportedOrderState model
   * @this {!ExportedOrderStateModel}
   * @returns {!FieldModelExportedOrderState} model ExportedOrderState model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ExportedOrderStateModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ExportedOrderStateModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ExportedOrderStateModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ExportedOrderStateModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelExportedOrderState.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ExportedOrderStateModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ExportedOrderStateModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ExportedOrderStateModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ExportedOrderStateModel}
   * @param {!ExportedOrderState} value ExportedOrderState value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ExportedOrderStateModel}
   * @param {!ExportedOrderState} value ExportedOrderState value, defaults is new ExportedOrderState()
   * @return {!object} Deserialized ExportedOrderState value and its size
   */
  deserialize (value = new ExportedOrderState()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ExportedOrderState(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ExportedOrderState(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ExportedOrderStateModel}
   * @param {!number} prev Previous ExportedOrderState model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ExportedOrderStateModel };
